import React from "react";

export default function GPUDeploymentChart() {
  // Set dimensions and margins (same as original)
  const width = 1000;
  const height = 700;
  const margin = { top: 70, right: 40, bottom: 50, left: 130 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // Date labels - assuming these are the dates you want to use
  // Adjust as needed for your actual dates
  const dates = Array.from({ length: 37 }, (_, i) => {
    const date = new Date(2025, 0, 1);
    date.setMonth(date.getMonth() + i);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  });

  // Data arrays from the provided input (with H100 equivalents applied)
  const H100s = [400000, 460000, 460000, 460000, 460000, 460000, 460000, 460000, 460000, 460000, 460000, 460000, 460000, 460000, 470000, 470000, 470000, 470000, 470000, 470000, 470000, 450000, 450000, 450000, 450000, 430000, 430000, 430000, 410000, 410000, 400000, 400000, 380000, 380000, 350000, 320000, 300000];

  const GB200s = [0, 0, 0, 0, 20000, 40000, 80000, 120000, 160000, 180000, 180000, 180000, 180000, 180000, 180000, 180000, 200000, 220000, 240000, 240000, 240000, 240000, 240000, 240000, 240000, 240000, 240000, 240000, 240000, 230000, 230000, 230000, 220000, 220000, 220000, 210000, 210000].map(x => x * 5);

  const GB300s = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 40000, 60000, 80000, 120000, 160000, 180000, 200000, 240000, 300000, 340000, 400000, 450000, 500000, 500000, 500000, 500000, 500000, 500000, 500000, 500000, 500000, 490000, 490000, 480000, 480000].map(x => x * 7.5);

  const VR200s = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 20000, 40000, 80000, 120000, 200000, 276000, 390000, 456000, 540000, 680000, 770000, 890000].map(x => x * 12);

  const InHouse = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 2000, 5000, 5000, 8000, 10000, 20000, 40000, 80000, 100000, 100000, 160000, 160000, 160000, 160000, 240000, 280000, 350000, 440000].map(x => x * 10);
  
  // Calculate cumulative values for stacked areas
  const calculateCumulativeValues = () => {
    const cumulative = [];
    for (let i = 0; i < dates.length; i++) {
      cumulative.push({
        h100: H100s[i],
        gb200: H100s[i] + GB200s[i],
        gb300: H100s[i] + GB200s[i] + GB300s[i],
        vr200: H100s[i] + GB200s[i] + GB300s[i] + VR200s[i],
        total: H100s[i] + GB200s[i] + GB300s[i] + VR200s[i] + InHouse[i]
      });
    }
    return cumulative;
  };
  
  const cumulativeValues = calculateCumulativeValues();

  // Choose select x-axis tick indices (showing fewer dates for clarity)
  const xTickIndices = [0, 6, 12, 18, 24, 30, 36];

  // Updated colors using the scheme from the original code
  const colors = {
    h100: "#000100",     // Dark gray/black (using compute color from original)
    gb200: "#508050",    // Medium green (using humanAlgo color from original)
    gb300: "#004000",    // Dark green (using aiAlgo color from original)
    vr200: "#AA0000",    // Red (new color)
    inhouse: "#0000AA",  // Blue (new color)
    axis: "#444444",     // Dark gray for axis
    text: "#333333"      // Dark gray text

  };// Define TypeScript types
  type CumulativeValue = {
    h100: number;
    gb200: number;
    gb300: number;
    vr200: number;
    total: number;
  };
  
  // Scales: x based on index, y based on value
  const xScale = (i: number): number => margin.left + (i / (dates.length - 1)) * innerWidth;
  
  // Linear scale for the y-axis
  const yScale = (value: number): number => {
    const maxValue = 20000000; // Max y-axis value of 20M
    return margin.top + innerHeight - (value / maxValue) * innerHeight;
  };
  
  // Generate x-values for all data points
  const xValues: number[] = dates.map((_, i) => xScale(i));
  
  // Generate area paths for stacked area chart
  const generateAreaPath = (
    getBottom: (values: CumulativeValue) => number,
    getTop: (values: CumulativeValue) => number
  ): string => {
    // Draw the top curve from left to right
    let path = cumulativeValues.map((values, i) => 
      `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(getTop(values))}`
    ).join(' ');
    
    // Then draw the bottom curve from right to left
    path += ' ' + cumulativeValues.slice().reverse().map((values, i) => 
      `L ${xScale(cumulativeValues.length - 1 - i)} ${yScale(getBottom(values))}`
    ).join(' ');
    
    // Close the path
    path += ' Z';
    
    return path;
  };
  
  // Generate area paths for each layer
  const h100Area: string = generateAreaPath(
    (): number => 0,
    (values: CumulativeValue): number => values.h100
  );
  
  const gb200Area: string = generateAreaPath(
    (values: CumulativeValue): number => values.h100,
    (values: CumulativeValue): number => values.gb200
  );
  
  const gb300Area: string = generateAreaPath(
    (values: CumulativeValue): number => values.gb200,
    (values: CumulativeValue): number => values.gb300
  );
  
  const vr200Area: string = generateAreaPath(
    (values: CumulativeValue): number => values.gb300,
    (values: CumulativeValue): number => values.vr200
  );
  
  const inhouseArea: string = generateAreaPath(
    (values: CumulativeValue): number => values.vr200,
    (values: CumulativeValue): number => values.total
  );

  // Calculate the total equivalent H100s at the end
  const totalEquivalentH100s = cumulativeValues[cumulativeValues.length - 1].total;

  return (
    <div className="w-full relative overflow-hidden mt-8 mb-8">
      <svg
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Chart Title */}
        <text
          x={margin.left}
          y={40}
          fontSize="32"
          fontFamily="sans-monospace"
          className="text-[32px] font-mono md:text-[32px] sm:text-[32px]"
          fill={colors.text}
        >
          GPU Deployment by Chip Type
        </text>

        {/* X-axis ticks and labels */}
        {xTickIndices.map((index) => {
          const x = xValues[index];
          return (
            <g key={`x-tick-${index}`}>
              <line 
                x1={x} 
                y1={height - margin.bottom} 
                x2={x} 
                y2={margin.top + 20} 
                stroke={colors.axis}
                strokeWidth="0.5" 
                strokeDasharray="5,5"
                opacity="0.15"
              />
              <text 
                x={x} 
                y={height - margin.bottom / 2} 
                textAnchor="middle" 
                className="text-[16px] font-mono md:text-[16px] sm:text-[16px]"
                fill={colors.text}
              >
                {dates[index]}
              </text>
            </g>
          );
        })}

        {/* Y-axis Label */}
        <text
          transform={`rotate(-90)`}
          y={10}
          x={-height / 2}
          dy="1em"
          textAnchor="middle"
          className="text-[22px] font-mono md:text-[22px] sm:text-[22px]"
          fill={colors.text}
        >
          H100 Equivalents (H100e)
        </text>

        {/* Horizontal grid lines */}
        {[2000000, 4000000, 6000000, 8000000, 10000000, 12000000, 14000000, 16000000, 18000000, 20000000].map((value, i) => {
          const y = yScale(value);
          if (y < margin.top || y > height - margin.bottom) return null;
          
          return (
            <g key={`grid-${i}`}>
              <line 
                x1={margin.left} 
                y1={y} 
                x2={width - margin.right} 
                y2={y} 
                stroke="#ddd" 
                strokeWidth="0.8" 
                strokeDasharray="5,5"
              />
              
              <text 
                x={margin.left - 10} 
                y={y + 4} 
                textAnchor="end" 
                className="text-[18px] font-mono md:text-[18px] sm:text-[18px]"
                fill={colors.text}
              >
                {(value / 1000000).toFixed(1)}M
              </text>
            </g>
          );
        })}

        {/* Stacked areas (filled with colors) */}
        <path 
          d={h100Area} 
          fill={colors.h100} 
          fillOpacity={0.9} 
          stroke="none"
        />
        <path 
          d={gb200Area} 
          fill={colors.gb200} 
          fillOpacity={0.9} 
          stroke="none"
        />
        <path 
          d={gb300Area} 
          fill={colors.gb300} 
          fillOpacity={0.9} 
          stroke="none"
        />
        <path 
          d={vr200Area} 
          fill={colors.vr200} 
          fillOpacity={0.9} 
          stroke="none"
        />
        <path 
          d={inhouseArea} 
          fill={colors.inhouse} 
          fillOpacity={0.9} 
          stroke="none"
        />

        {/* Boundary lines for clarity */}
        <path 
          d={cumulativeValues.map((values, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(values.h100)}`).join(' ')} 
          fill="none" 
          stroke={colors.h100} 
          strokeWidth=".5" 
          opacity="0.5"
        />
        <path 
          d={cumulativeValues.map((values, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(values.gb200)}`).join(' ')} 
          fill="none" 
          stroke={colors.gb200} 
          strokeWidth=".5" 
          opacity="0.5"
        />
        <path 
          d={cumulativeValues.map((values, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(values.gb300)}`).join(' ')} 
          fill="none" 
          stroke={colors.gb300} 
          strokeWidth=".5" 
          opacity="0.5"
        />
        <path 
          d={cumulativeValues.map((values, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(values.vr200)}`).join(' ')} 
          fill="none" 
          stroke={colors.vr200} 
          strokeWidth=".5" 
          opacity="0.5"
        />
        <path 
          d={cumulativeValues.map((values, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(values.total)}`).join(' ')} 
          fill="none" 
          stroke="#000" 
          strokeWidth=".5" 
          opacity="0.5"
        />

        {/* Legend */}
        <g transform={`translate(${margin.left+15}, 75)`}>
          {/* H100s */}
          <rect x="0" y="0" width="20" height="20" fill={colors.h100} />
          <text x="30" y="15" fill={colors.text} className="text-[16px] font-mono">H100s</text>
          
          {/* GB200s */}
          <rect x="0" y="30" width="20" height="20" fill={colors.gb200} />
          <text x="30" y="45" fill={colors.text} className="text-[16px] font-mono">GB200s</text>
          
          {/* GB300s */}
          <rect x="0" y="60" width="20" height="20" fill={colors.gb300} />
          <text x="30" y="75" fill={colors.text} className="text-[16px] font-mono">GB300s</text>
          
          {/* VR200s */}
          <rect x="0" y="90" width="20" height="20" fill={colors.vr200} />
          <text x="30" y="105" fill={colors.text} className="text-[16px] font-mono">VR200s</text>
          
          {/* In-house */}
          <rect x="0" y="120" width="20" height="20" fill={colors.inhouse} />
          <text x="30" y="135" fill={colors.text} className="text-[16px] font-mono">Inference chips</text>
        </g>
      </svg>
    </div>
  );
}